import React, { useEffect, useMemo, useRef, useState } from 'react'
import classes from './SearchResults.module.css'
import Footer from '../../components/Footer/Footer'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { ReactComponent as RightIcon } from '../../assets/icons/right.svg'
import { ReactComponent as CancelIcon } from '../../assets/icons/close.svg'
import { ReactComponent as DownIcon } from '../../assets/icons/down-caret.svg'
import { ReactComponent as OptionsIcon } from '../../assets/icons/options.svg'
import SearchFilters from '../../components/Search/SearchFilters'
import SearchCard from '../../components/Search/SearchCard'
import PageChanger from '../../components/PageChanger/PageChanger'
import { AuthenticatedHeader } from '../../components/Header/AuthenticatedHeader'
import { useDispatch, useSelector } from 'react-redux'
import { getCategories } from '../Course/categoriesApi'
import { categoriesSelectors } from '../Course/categoriesSlice'
import { premiumFilters, priceFilters, typeFilters } from './searchFilters'
import { getResults } from './searchApi'
import { searchActions, searchSelectors } from './searchSlice'
import SearchCardLoader from '../../components/loaders/SearchCardLoader'
import notification from '../../components/Notification/notification'
import Sort from '../../components/Form/SortBy/Sort'
import Filters from '../../components/Filter/Filters'
import Empty from '../../components/Empty/Empty'
import coursePlaceholder from '../../assets/images/course-placeholder-gray.svg'
import classPlaceholder from '../../assets/images/class-placeholder-gray.svg'
import { useHideOnClickOutside } from '../../hooks/useHideOnClickOutside'

const getParams = location => {
    const params = new URLSearchParams(location.search)
    return {
        typeFilter: params.get('type')?.toLowerCase() || 'course',
        priceFilter: params.get('price')?.toLowerCase() || '',
        categoryFilter: params.get('category')?.toLowerCase() || '',
        search: params.get('search')?.toLowerCase() || '',
        premiumFilter: params.get('premium')?.toLowerCase() || '',
    }
}

const SearchResults = ({ location, history }) => {
    const {
        typeFilter,
        priceFilter,
        categoryFilter,
        search,
        premiumFilter,
    } = useMemo(() => getParams(location), [location])

    const sortOptions = useMemo(
        () =>
            [
                {
                    id: 'name',
                    text: 'Alphabetical Order',
                },
                {
                    id: 'enrolled',
                    text: 'Number of students',
                },
                {
                    id: 'price',
                    text: 'Item price',
                },
                typeFilter === 'class'
                    ? {
                          id: 'startdate',
                          text: 'Class Start Date',
                      }
                    : null,
            ].filter(option => option),
        [typeFilter]
    )

    const params = useMemo(() => new URLSearchParams(location.search), [
        location.search,
    ])

    const redirect = (filter, value) => {
        params.delete(filter)
        params.append(filter, value)
        history.push(`${location.pathname}?${params.toString()}`)
    }

    const cancelFilter = id => {
        params.delete(id)
        history.push(`${location.pathname}?${params.toString()}`)
    }

    const dispatch = useDispatch()

    const categories = useSelector(categoriesSelectors.categories)
    const categoriesFetching = useSelector(categoriesSelectors.isFetching)
    const categoriesHasError = useSelector(categoriesSelectors.hasError)
    const categoriesErrorMessage = useSelector(categoriesSelectors.errorMessage)

    const filters = useMemo(() => {
        const typeText =
            typeFilters.find(({ id }) => id === typeFilter)?.text || ''
        const priceText =
            priceFilters.find(({ id }) => id === priceFilter)?.text || ''
        const categoryText =
            categories?.find(({ id }) => String(id) === categoryFilter)?.text ||
            ''
        const premiumText =
            premiumFilters.find(({ id }) => id === premiumFilter)?.text || ''
        return [
            {
                text: typeText,
                id: 'type',
                cancel: false,
            },
            {
                text: priceText,
                id: 'price',
            },
            {
                text: categoryText,
                id: 'category',
            },
            {
                text: search,
                id: 'search',
                cancel: false,
            },
            {
                text: premiumText,
                id: 'premium',
            },
        ].filter(({ text }) => text.trim())
    }, [
        typeFilter,
        priceFilter,
        categories,
        search,
        categoryFilter,
        premiumFilter,
    ])

    const mobileFilterOptions = useMemo(
        () => [
            {
                id: 'type',
                text: 'Type',
                options: typeFilters,
            },
            {
                id: 'premium',
                text: 'Premium',
                options: premiumFilters,
            },
            {
                id: 'price',
                text: 'Price',
                options: priceFilters,
            },
            {
                id: 'category',
                text: 'Category',
                options:
                    categories?.map(category => ({
                        ...category,
                        id: category.catid || 'all',
                    })) || [],
            },
        ],
        [categories]
    )

    const mobileActiveFilters = useMemo(() => {
        return {
            type: typeFilter,
            price: priceFilter,
            category: categoryFilter,
            premium: premiumFilter,
        }
    }, [typeFilter, priceFilter, premiumFilter, categoryFilter])

    const [mobileFilterActive, setMobileFilterActive] = useState(false)

    useEffect(() => {
        dispatch(getCategories('search'))
    }, [dispatch])

    const results = useSelector(searchSelectors.results)
    const isFetching = useSelector(searchSelectors.isFetching)
    const paging = useSelector(searchSelectors.paging)

    const hasError = useSelector(searchSelectors.hasError)
    const errorMessage = useSelector(searchSelectors.errorMessage)

    useEffect(() => {
        if (hasError) {
            notification.failure({
                title: 'Could not fetch search results',
                description: errorMessage,
            })
        }

        if (categoriesHasError) {
            notification.failure({
                title: 'Could not fetch search results',
                description: categoriesErrorMessage,
            })
            dispatch(searchActions.getSearchResultsRequestSuccess([]))
        }
    }, [hasError, categoriesHasError])

    const [page, setPage] = useState(0)

    useEffect(() => {
        setPage(paging.pageNo)
    }, [paging.pageNo])

    const [sort, setSort] = useState('Alphabetical Order')
    const [sortId, setSortId] = useState('name')

    const setSortHelper = newSort => {
        setSort(sortOptions.find(({ id }) => id === newSort)?.text || '')
        setSortId(newSort)
    }

    useEffect(() => {
        if (!categoriesFetching) {
            const params = {
                type: typeFilter,
                price: priceFilter,
                catid: categoryFilter !== 'all' ? categoryFilter : null,
                premium: premiumFilter,
                query: search,
                pageNo: page,
                sort: sortId || 'name',
            }
            Object.entries(params).forEach(([key, value]) => {
                if (!value) {
                    delete params[key]
                }
            })
            dispatch(getResults(params))
        }
    }, [
        categoriesFetching,
        dispatch,
        typeFilter,
        priceFilter,
        categoryFilter,
        premiumFilter,
        search,
        page,
        sortId,
    ])

    return (
        <>
            <AuthenticatedHeader />
            <main>
                <Filters
                    filters={mobileFilterOptions}
                    activeFilters={mobileActiveFilters}
                    redirect={redirect}
                    open={mobileFilterActive}
                    onClose={() => setMobileFilterActive(false)}
                />
                <section className={classes.topBar}>
                    <div className={classes.topLinks}>
                        <Link
                            className={classNames(
                                classes.topLink,
                                classes.topLinkBold
                            )}
                            to={''}
                        >
                            Home
                        </Link>
                        <RightIcon className={classes.topLinkDivider} />
                        <Link className={classes.topLink} to={''}>
                            Search
                        </Link>
                    </div>
                    <h1 className={classes.topTitle}>
                        Search Results for “{search}”
                    </h1>
                </section>
                <section className={classes.topBarMobile}>
                    <h1 className={classes.topBarMobileText}>“{search}”</h1>
                </section>
                <section className={classes.filtersMobile}>
                    <SortComponent
                        sortOptions={sortOptions}
                        sort={sort}
                        setSort={setSortHelper}
                    />
                    <span className={classes.filtersMobileSplit} />
                    <div
                        className={classes.filterMobile}
                        onClick={_ => setMobileFilterActive(true)}
                    >
                        <OptionsIcon />
                        <p className={classes.filterMobileText}>Filter</p>
                    </div>
                </section>
                <div className={classes.pageGrid}>
                    <section className={classes.filterData}>
                        <h4 className={classes.filterTitle}>Filter Element</h4>
                        <div className={classes.filters}>
                            {filters.map(({ text, id, cancel = true }) => (
                                <Filter
                                    onCancel={cancelFilter}
                                    id={id}
                                    cancel={cancel}
                                >
                                    {text}
                                </Filter>
                            ))}
                        </div>
                        <span className={classes.dividerLine} />
                        <div className={classes.filterCards}>
                            <SearchFilters
                                header={'EVENT TYPE'}
                                filters={typeFilters}
                                activeId={typeFilter}
                                variants={'blue'}
                                onClick={id => redirect('type', id)}
                            />
                            <SearchFilters
                                header={'CATEGORIES'}
                                filters={
                                    categories?.map(category => ({
                                        ...category,
                                        id: category.catid || 'all',
                                    })) || []
                                }
                                activeId={categoryFilter}
                                variants={'blue'}
                                onClick={id => redirect('category', id)}
                                isFetching={categoriesFetching}
                            />
                            <SearchFilters
                                header={'PRICE'}
                                filters={priceFilters}
                                activeId={priceFilter}
                                variants={'red'}
                                onClick={id => redirect('price', id)}
                            />
                            <SearchFilters
                                header={'PREMIUM'}
                                filters={premiumFilters}
                                activeId={premiumFilter}
                                variants={'red'}
                                onClick={id => redirect('premium', id)}
                            />
                        </div>
                    </section>
                    <section>
                        <div className={classes.resultsTop}>
                            <h4 className={classes.resultsFound}>
                                <span className={classes.blue}>
                                    {paging.totalRecordCount}{' '}
                                </span>
                                items found
                            </h4>
                            <Sort
                                options={sortOptions.filter(option => option)}
                                sort={sort}
                                setSort={setSortHelper}
                            />
                        </div>
                        <div className={classes.resultCards}>
                            {isFetching
                                ? [...Array(5)].map((_, idx) => (
                                      <SearchCardLoader key={idx} />
                                  ))
                                : results.map(
                                      ({
                                          title,
                                          category,
                                          price,
                                          numberOfLectures,
                                          downloadableMaterial,
                                          enrolled,
                                          coursecode,
                                          classid,
                                          date,
                                          type,
                                          thumbnail,
                                      }) => (
                                          <SearchCard
                                              imageUrl={thumbnail}
                                              title={title}
                                              category={category}
                                              price={price}
                                              lectures={numberOfLectures}
                                              downloadable={
                                                  downloadableMaterial
                                              }
                                              date={date}
                                              students={enrolled}
                                              key={coursecode || classid}
                                              id={coursecode || classid}
                                              type={type}
                                          />
                                      )
                                  )}
                        </div>
                        {!isFetching && results.length === 0 && (
                            <Empty
                                icon={
                                    typeFilter === 'class'
                                        ? classPlaceholder
                                        : coursePlaceholder
                                }
                                text={'No Items Found'}
                                className={classes.empty}
                            />
                        )}
                        <div className={classes.pageChangerWrapper}>
                            <PageChanger
                                currentPage={page}
                                onPageChange={setPage}
                                pageCount={paging.pageCount}
                            />
                        </div>
                    </section>
                </div>
            </main>
            <Footer />
        </>
    )
}

const SortComponent = ({ sortOptions, sort, setSort }) => {
    const [active, setActive] = useState(false)

    const optionsRef = useRef()

    useHideOnClickOutside({
        open: active,
        el: optionsRef.current,
        hide: _ => setActive(false),
    })

    return (
        <div className={classes.filterMobile}>
            <OptionsIcon />
            <div onClick={() => setActive(!active)}>
                <p className={classes.filterMobileText}>Sort By</p>
                <p
                    className={classNames(
                        classes.filterMobileText,
                        classes.filterMobileTextBold
                    )}
                >
                    {sort}
                </p>
            </div>
            <div
                className={classNames(
                    classes.sort,
                    active && classes.sortActive
                )}
                ref={optionsRef}
            >
                {sortOptions.map(({ text, id }) => (
                    <button
                        className={classes.sortText}
                        onClick={() => {
                            setSort(id)
                            setActive(false)
                        }}
                    >
                        {text}
                    </button>
                ))}
            </div>
        </div>
    )
}

const Filter = ({ children, onCancel, id, cancel = true }) => (
    <span className={classes.filter}>
        <span className={classes.filterText}>{children}</span>
        {cancel && (
            <button
                className={classes.filterCancel}
                onClick={() => onCancel(id)}
            >
                <CancelIcon className={classes.filterCancelIcon} />
            </button>
        )}
    </span>
)

export default SearchResults
